/**
 * Chart colour.
 *
 * The charts were painted with a hardcoded set of stock Tailwind hexes —
 * #2563eb blue, #7c3aed violet, #059669 green. Three problems with that:
 *
 *   1. None of them were Lynk colours, so the analytics screens looked like a
 *      different product from the rest of the app.
 *   2. Being literal hexes, they didn't move when the theme did. A palette
 *      tuned for white paper stayed exactly as saturated on the near-black
 *      dark surface, where it glared.
 *   3. Status colours and categorical colours were mixed into one bag, so
 *      "accepted" and "series 3" could come out the same green by accident.
 *
 * So this module splits the two jobs.
 *
 * `statusColor(name)` is for values that *mean* something — accepted, rejected,
 * overdue. Those must agree with the badges and rails elsewhere in the app, so
 * they resolve from the same semantic CSS variables the rest of the UI uses.
 *
 * `categorical(n)` is for series with no inherent meaning — traffic sources,
 * countries, browsers. Order carries no message, so the only requirements are
 * that neighbours stay distinguishable and the set stays calm next to the
 * brand. It's anchored on grenat and deliberately a little desaturated:
 * eight fully saturated hues fighting each other is the "AI dashboard" look the
 * brief rules out.
 */

/** Read a CSS custom property off the document root. */
function cssVar(name, fallback) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name)?.trim();
  return v || fallback;
}

function isDarkTheme() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/**
 * Categorical series colours.
 *
 * Anchored on grenat, then stepped around the wheel in unequal jumps —
 * even spacing produces a rainbow, which reads as decoration rather than data.
 * The dark set isn't the light set lightened uniformly; the greens and reds
 * need more lift than the darks to hold the same apparent weight on near-black.
 */
const CATEGORICAL_LIGHT = [
  '#33070F', // grenat — the brand anchor
  '#7A5C1C', // gilt-brown
  '#2F5D3A', // forest
  '#1F4A5F', // deep teal — the one cool step, and it earns its place: eight
  //            warm hues alone stop being separable by the fourth series
  '#8A3A5E', // mulberry
  '#B0632A', // burnt sienna
  '#4A4636', // olive drab
  '#6B4E7A', // aubergine
];

const CATEGORICAL_DARK = [
  '#C9756B', // lifted claret — the dark-mode primary
  '#D9AE4E', // gilt, which finally works on Night
  '#6FAF7E',
  '#6BA8C0',
  '#D18AA8',
  '#E0A06B',
  '#A8A184',
  '#B39AC4',
];

export function categoricalPalette() {
  return isDarkTheme() ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
}

/** Nth categorical colour, wrapping if a series runs longer than the palette. */
export function categorical(index) {
  const p = categoricalPalette();
  return p[((index % p.length) + p.length) % p.length];
}

/**
 * Semantic colours, resolved from the live theme so charts and badges never
 * disagree about what "accepted" looks like.
 */
export function semanticColors() {
  return {
    primary: cssVar('--color-primary', '#33070F'),
    success: cssVar('--color-success', '#2F5D3A'),
    warning: cssVar('--color-warning', '#8A5A12'),
    error: cssVar('--color-error', '#A83A17'),
    muted: cssVar('--color-muted-foreground', '#6A6052'),
    accent: cssVar('--color-accent', isDarkTheme() ? '#D9AE4E' : '#7A5C1C'),
    sky: isDarkTheme() ? '#6BA8C0' : '#1F4A5F',
  };
}

/**
 * Status → colour. Anything unmapped falls back to muted rather than to a
 * random hue, so an unrecognised status reads as "no signal" instead of
 * accidentally claiming meaning it doesn't have.
 */
export function statusColor(name) {
  const c = semanticColors();
  const map = {
    // Submissions
    draft: c.muted,
    submitted: c.primary,
    under_review: c.accent,
    accepted: c.success,
    rejected: c.error,
    revision_required: c.warning,
    withdrawn: c.muted,
    // Conferences
    published: c.primary,
    submission_open: c.success,
    submission_closed: c.warning,
    review_phase: c.accent,
    decision_made: c.sky,
    completed: c.success,
    cancelled: c.error,
    // Reviews
    pending: c.muted,
    unknown: c.muted,
  };
  return map[name] || c.muted;
}

/** Axis, grid and tooltip chrome, so charts sit on the surface rather than on top of it. */
export function chartChrome() {
  return {
    grid: cssVar('--color-border', '#D7CDB5'),
    axis: cssVar('--color-muted-foreground', '#6A6052'),
    tooltipBg: cssVar('--color-card', '#F8F2E2'),
    tooltipBorder: cssVar('--color-border', '#D7CDB5'),
    tooltipText: cssVar('--color-foreground', '#211C16'),
  };
}

export default { categorical, categoricalPalette, statusColor, semanticColors, chartChrome };
